import { config } from '@gen2/config';
import { AxiosPromise } from 'axios';
import axios from '../axios.request';

export type TIntegrationType =
  | 'google'
  | 'microsoft'
  | 'box'
  | 'box_account'
  | 'sharepoint'
  | 'google_account'
  | 'hubspot'
  | 'salesforce';

/**
 * Maps legacy integration types to new provider-specific endpoints
 * Returns the new endpoint path for granular permissions
 */
export const getIntegrationEndpoint = (type: TIntegrationType): string => {
  const endpointMap: Record<string, string> = {
    google: 'google', // Google Drive user-level endpoint
    microsoft: 'microsoft', // OneDrive Business user-level endpoint
    box: 'box', // Box user-level endpoint
    box_account: 'box-account', // Box Account level endpoint
    sharepoint: 'sharepoint', // SharePoint integration endpoint
    google_account: 'google-account', // Google Drive Account level endpoint
  };

  return endpointMap[type] || type;
};

export interface ConnectIntPayload {
  code: string;
  redirect_uri: string;
  type?: TIntegrationType;
}

export interface ConnectIntResponse {
  message: string;
  data: {
    is_resync_enabled: boolean;
  };
}

export const connectInt = ({
  type,
  ...payload
}: ConnectIntPayload): AxiosPromise<ConnectIntResponse> => {
  const endpoint = getIntegrationEndpoint(type || 'google');
  return axios.request({
    url: `${config.apiVersion.v1}/integrations/${endpoint}/connect`,
    data: payload,
    method: 'post',
  });
};

export interface DisconnectIntPayload {
  type: TIntegrationType;
}

export interface DisconnectIntResponse {
  message: string;
}

export const disconnectInt = (
  payload: DisconnectIntPayload,
): AxiosPromise<DisconnectIntPayload> => {
  const endpoint = getIntegrationEndpoint(payload.type);
  return axios.request({
    url: `${config.apiVersion.v1}/integrations/${endpoint}/disconnect`,
    data: payload,
    method: 'post',
  });
};

export interface GetIntFoldersParams {
  folder_id?: string;
  site_id?: string;
  document_id?: string;
  search?: string;
}

export interface IntFolder {
  id: string;
  name: string;
  path: string;
  document_id?: string;
}

export interface GetIntFoldersResponse {
  data: {
    folders: IntFolder[];
    parent: IntFolder;
  };
  message: string;
}

export const getStorageFolders = (
  vendor: TIntegrationType,
  params?: GetIntFoldersParams,
): AxiosPromise<GetIntFoldersResponse> => {
  const endpoint = getIntegrationEndpoint(vendor);
  return axios.request({
    url: `${config.apiVersion.v1}/integrations/${endpoint}/folders`,
    params,
    method: 'get',
  });
};

export interface GetIntSettingsResponse {
  data: {
    folder: {
      id: string;
      name: string;
      path: string;
    };
    site_id?: string;
    site_name?: string;
    document_id?: string;
    document_name?: string;
  };
  message: string;
}

export const getStorageSettings = (
  vendor: TIntegrationType,
): AxiosPromise<GetIntSettingsResponse> => {
  const endpoint = getIntegrationEndpoint(vendor);
  return axios.request({
    url: `${config.apiVersion.v1}/integrations/${endpoint}`,
    method: 'get',
  });
};

export interface SaveIntFolderPayload {
  vendor: TIntegrationType;
  directory_id: string;
  site_id?: string;
  document_id?: string;
}

export const saveIntFolder = ({
  vendor,
  ...payload
}: SaveIntFolderPayload): AxiosPromise<DisconnectIntPayload> => {
  const endpoint = getIntegrationEndpoint(vendor);
  return axios.request({
    url: `${config.apiVersion.v1}/integrations/${endpoint}`,
    data: payload,
    method: 'post',
  });
};

export interface CreateIntFolderPayload {
  vendor: TIntegrationType;
  folder_name: string;
  parent_folder_id?: string;
  site_id?: string;
  document_id?: string;
}

export interface CreateIntFolderResponse {
  data: {
    folder: IntFolder;
  };
  message: string;
}

export const createIntFolder = ({
  vendor,
  ...payload
}: CreateIntFolderPayload): AxiosPromise<CreateIntFolderResponse> => {
  const endpoint = getIntegrationEndpoint(vendor);
  return axios.request({
    url: `${config.apiVersion.v1}/integrations/${endpoint}/folders`,
    data: payload,
    method: 'post',
  });
};

export interface SharepointSitesRequest {
  site_id: string;
}

export interface SharepointSite {
  id: string;
  name: string;
  path: string;
}

export interface SharepointSitesResponse {
  data: {
    sites: Array<SharepointSite>;
  };
  message: string;
}

export const getSharepointSites = (): AxiosPromise<SharepointSitesResponse> => {
  return axios.request({
    url: `${config.apiVersion.v1}/integrations/sharepoint/sites`,
    method: 'get',
  });
};

export type SharepointDocumentLibrary = {
  id: string;
  name: string;
  path: string;
};

export type SharepointDocumentLibrariesResponse = {
  data: {
    document_libraries: SharepointDocumentLibrary[];
  };
  message: string;
};

export const getSharepointDocumentLibraries = (
  siteId: string,
): AxiosPromise<SharepointDocumentLibrariesResponse> => {
  return axios.request({
    url: `${config.apiVersion.v1}/integrations/sharepoint/document-libraries`,
    method: 'get',
    params: {
      site_id: siteId,
    },
  });
};

export const getWorkatoEmbeddedLink = (
  type: TIntegrationType,
): AxiosPromise<{ data: { link: string } }> => {
  return axios.request({
    url: `${config.apiVersion.v1}/managed-integrations/${type}/link`,
    method: 'get',
  });
};

export const getWorkatoManagedConnection = (
  type: TIntegrationType,
): AxiosPromise<{
  data: {
    connections: {
      id: string;
      name: string;
    }[];
  };
}> => {
  return axios.request({
    url: `${config.apiVersion.v1}/managed-integrations/connections`,
    method: 'get',
    params: {
      provider: type,
    },
  });
};

export const getAllWorkatoManagedConnections = (): AxiosPromise<{
  data: {
    connections: {
      id: string;
    }[];
  };
}> => {
  return axios.request({
    url: `${config.apiVersion.v1}/managed-integrations/connections`,
    method: 'get',
  });
};

export interface SyncFoldersResponse {
  message: string;
}

export const syncFolders = ({
  vendor,
}: {
  vendor: TIntegrationType;
}): AxiosPromise<SyncFoldersResponse> => {
  const endpoint = getIntegrationEndpoint(vendor);
  return axios.request({
    url: `${config.apiVersion.v1}/integrations/${endpoint}/resync`,
    method: 'post',
  });
};
